import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';

const Movies = () => {
  const [movies, setMovies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingMovie, setEditingMovie] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    genre: [],
    releaseYear: '',
    director: '',
    cast: [],
    poster: '',
    trailer: '',
    videoUrl: '',
    videoCode: '',
    duration: '',
    language: ['Français'],
    rating: { imdb: 0, site: 0 },
    isTrending: false,
    isNewRelease: false,
    isAnimation: false
  });
  const [uploadMethod, setUploadMethod] = useState('direct'); // 'direct' or 'voe'
  const [voeUrl, setVoeUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [tmdbUrl, setTmdbUrl] = useState('');
  const [tmdbLanguage, setTmdbLanguage] = useState('fr-FR'); // Default to French
  const [loadingTmdb, setLoadingTmdb] = useState(false);
  
  // Search and pagination states
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [moviesPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    // Debounce search to avoid too many API calls
    const delayDebounceFn = setTimeout(() => {
      fetchMovies(currentPage, searchTerm);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, currentPage]);

  const fetchMovies = async (page = 1, search = '') => {
    try {
      const res = await axios.get('/api/admin/movies', {
        params: {
          page: page,
          limit: moviesPerPage,
          search: search
        }
      });
      
      if (res.data.pagination) {
        setMovies(res.data.movies);
        setTotalPages(res.data.pagination.totalPages);
        setTotalItems(res.data.pagination.totalItems);
        setCurrentPage(res.data.pagination.currentPage);
      } else {
        // Fallback for old API structure if something goes wrong
        setMovies(res.data);
        setTotalPages(1);
        setTotalItems(res.data.length);
      }
    } catch (error) {
      console.error('Error fetching movies:', error);
    }
  };

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: parseFloat(value) || 0 }
      }));
    } else if (name === 'genre' || name === 'language') {
      setFormData(prev => ({
        ...prev,
        [name]: value.split(',').map(item => item.trim()).filter(item => item)
      }));
    } else if (name === 'cast') {
      // Cast can be array of objects (from TMDB) or comma-separated string (manual input)
      // When user types, convert to array of objects with name only
      setFormData(prev => ({
        ...prev,
        [name]: value.split(',').map(item => item.trim()).filter(item => item).map(name => ({
          name: name,
          image: ''
        }))
      }));
    } else if (name === 'videoUrl' && uploadMethod === 'direct') {
      // Auto-extract Bunny Stream video ID from URL if it's a Bunny Stream URL
      // Format: https://iframe.mediadelivery.net/play/580230/2449ac8c-9d76-42b7-b044-9057c839df5c
      const bunnyMatch = value.match(/iframe\.mediadelivery\.net\/play\/\d+\/([a-f0-9-]+)/i);
      if (bunnyMatch && bunnyMatch[1]) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          videoCode: bunnyMatch[1] // Auto-extract video ID (UUID)
        }));
      } else {
        // If it's just a UUID, use it directly
        const uuidMatch = value.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i);
        if (uuidMatch) {
          setFormData(prev => ({
            ...prev,
            [name]: value,
            videoCode: value // Use UUID directly as video ID
          }));
        } else {
          setFormData(prev => ({ ...prev, [name]: value }));
        }
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Fetch movie data from TMDB
  const fetchTmdbData = async () => {
    if (!tmdbUrl || !tmdbUrl.includes('themoviedb.org/movie/')) {
      alert('Veuillez entrer une URL de film TMDB valide (ex: https://www.themoviedb.org/movie/12345)');
      return;
    }

    setLoadingTmdb(true);
    try {
      const res = await axios.post('/api/admin/tmdb/fetch', { 
        url: tmdbUrl,
        language: tmdbLanguage 
      });
      const tmdbData = res.data;

      // Auto-fill from TMDB; keep Bunny video fields when editing an existing film
      setFormData(prev => ({
        ...prev,
        title: tmdbData.title || prev.title,
        description: tmdbData.description || prev.description,
        genre: tmdbData.genre && tmdbData.genre.length > 0 ? tmdbData.genre : prev.genre,
        releaseYear: tmdbData.releaseYear || prev.releaseYear,
        director: tmdbData.director || prev.director,
        cast: tmdbData.cast && tmdbData.cast.length > 0 ? tmdbData.cast : prev.cast,
        poster: tmdbData.poster || prev.poster,
        trailer: tmdbData.trailer || prev.trailer,
        duration: tmdbData.duration || prev.duration,
        language: ['Français'],
        rating: {
          imdb: tmdbData.rating?.imdb || prev.rating.imdb,
          site: tmdbData.rating?.site || prev.rating.site
        },
        ...(editingMovie
          ? {
              videoUrl: prev.videoUrl,
              videoCode: prev.videoCode
            }
          : {})
      }));

      alert(
        editingMovie
          ? 'Film actualisé depuis TMDB (affiche, infos, casting). La vidéo Bunny en place n’a pas été modifiée.'
          : 'Données du film chargées depuis TMDB ! Vous pouvez modifier les champs avant d’enregistrer.'
      );
    } catch (error) {
      console.error('TMDB fetch error:', error);
      alert(error.response?.data?.message || 'Échec du chargement des données du film depuis TMDB');
    } finally {
      setLoadingTmdb(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const payload = { ...formData, isAnimation: !!formData.isAnimation };
      if (tmdbUrl.trim()) payload.tmdbUrl = tmdbUrl.trim();
      if (editingMovie) {
        await axios.put(`/api/admin/movies/${editingMovie._id}`, payload);
      } else {
        await axios.post('/api/admin/movies', payload);
      }
      setShowForm(false);
      setEditingMovie(null);
      setTmdbUrl('');
      setFormData({
        title: '',
        description: '',
        genre: [],
        releaseYear: '',
        director: '',
        cast: [],
        poster: '',
        trailer: '',
        videoUrl: '',
        videoCode: '',
        duration: '',
        language: ['Français'],
        rating: { imdb: 0, site: 0 },
        isTrending: false,
        isNewRelease: false,
        isAnimation: false
      });
      fetchMovies(currentPage, searchTerm);
    } catch (error) {
      console.error('Error saving movie:', error);
      alert('Échec de l\'enregistrement du film');
    } finally {
      setUploading(false);
    }
  };

  const handleVoeUpload = async () => {
    // This function is kept for backward compatibility
    // For Bunny Stream, users should paste the video ID or full URL directly
    if (!voeUrl) {
      alert('Veuillez entrer un ID vidéo Bunny Stream ou une URL');
      return;
    }

    // Extract video ID from URL or use directly if it's a UUID
    const bunnyMatch = voeUrl.match(/iframe\.mediadelivery\.net\/play\/\d+\/([a-f0-9-]+)/i);
    const videoId = bunnyMatch ? bunnyMatch[1] : (voeUrl.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i) ? voeUrl : null);
    
    if (videoId) {
      setFormData(prev => ({ ...prev, videoUrl: voeUrl, videoCode: videoId }));
      alert('ID vidéo Bunny Stream enregistré : ' + videoId);
      setVoeUrl('');
    } else {
      alert('Veuillez entrer un ID vidéo Bunny Stream valide (UUID) ou l\'URL complète');
    }
  };

  const handleEdit = (movie) => {
    setEditingMovie(movie);
    setFormData({
      title: movie.title || '',
      description: movie.description || '',
      genre: movie.genre || [],
      releaseYear: movie.releaseYear || '',
      director: movie.director || '',
      cast: movie.cast || [],
      poster: movie.poster || '',
      trailer: movie.trailer || '',
      videoUrl: movie.videoUrl || '',
      videoCode: movie.videoCode || '',
      duration: movie.duration || '',
      language: movie.language || [],
      rating: movie.rating || { imdb: 0, site: 0 },
      isTrending: movie.isTrending || false,
      isNewRelease: movie.isNewRelease || false,
      isAnimation: movie.isAnimation || false
    });
    setTmdbUrl(movie.tmdbUrl || '');
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce film ?')) return;

    try {
      await axios.delete(`/api/admin/movies/${id}`);
      fetchMovies(currentPage, searchTerm);
    } catch (error) {
      console.error('Error deleting movie:', error);
      alert('Échec de la suppression du film');
    }
  };

  return (
    <div className="min-h-screen bg-[#141414]">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white">Gérer les Films</h1>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingMovie(null);
              setTmdbUrl('');
              setFormData({
                title: '',
                description: '',
                genre: [],
                releaseYear: '',
                director: '',
                cast: [],
                poster: '',
                trailer: '',
                videoUrl: '',
                videoCode: '',
                duration: '',
                language: ['Français'],
                rating: { imdb: 0, site: 0 },
                isTrending: false,
                isNewRelease: false,
                isAnimation: false
              });
            }}
            className="px-6 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition font-semibold"
          >
            + Ajouter un Film
          </button>
        </div>

        {showForm && (
          <div className="bg-[#1A1A1A] rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-6">
              {editingMovie ? 'Modifier le Film' : 'Ajouter un Nouveau Film'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* TMDB */}
              <div className="bg-[#1A1A1A] p-4 rounded-lg mb-4 border border-gray-700">
                <label className="block text-white mb-2 font-semibold">
                  🎬 The Movie Database (TMDB)
                </label>
                <p className="text-gray-400 text-sm mb-3">
                  {editingMovie
                    ? "Actualisez l'affiche et les infos depuis TMDB sans perdre l'ID vidéo Bunny. Enregistrez l'URL pour la prochaine fois."
                    : "Collez l'URL TMDB pour préremplir la fiche. L'URL peut être enregistrée avec le film."}
                </p>
                <div className="flex flex-wrap gap-2 mb-2">
                  <input
                    type="url"
                    value={tmdbUrl}
                    onChange={(e) => setTmdbUrl(e.target.value)}
                    placeholder="https://www.themoviedb.org/movie/12345-..."
                    className="flex-1 min-w-[200px] px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                  />
                  <select
                    value={tmdbLanguage}
                    onChange={(e) => setTmdbLanguage(e.target.value)}
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                  >
                    <option value="fr-FR">🇫🇷 Français</option>
                    <option value="en-US">🇺🇸 Anglais</option>
                    <option value="es-ES">🇪🇸 Espagnol</option>
                    <option value="de-DE">🇩🇪 Allemand</option>
                    <option value="it-IT">🇮🇹 Italien</option>
                    <option value="pt-PT">🇵🇹 Portugais</option>
                  </select>
                  <button
                    type="button"
                    onClick={fetchTmdbData}
                    disabled={loadingTmdb || !tmdbUrl}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    {loadingTmdb
                      ? 'Chargement...'
                      : editingMovie
                        ? 'Actualiser depuis TMDB'
                        : 'Remplir depuis TMDB'}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white mb-2">Titre *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white mb-2">Année de sortie *</label>
                  <input
                    type="number"
                    name="releaseYear"
                    value={formData.releaseYear}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-white mb-2">Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white mb-2">Genre (séparés par des virgules) *</label>
                  <input
                    type="text"
                    name="genre"
                    value={formData.genre.join(', ')}
                    onChange={handleInputChange}
                    placeholder="Action, Drame, Comédie"
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white mb-2">Réalisateur *</label>
                  <input
                    type="text"
                    name="director"
                    value={formData.director}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-white mb-2">Casting (séparés par des virgules)</label>
                <input
                  type="text"
                  name="cast"
                  value={formData.cast.map(actor => typeof actor === 'string' ? actor : actor.name).join(', ')}
                  onChange={handleInputChange}
                  placeholder="Acteur 1, Acteur 2, Acteur 3"
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                />
                {formData.cast.some(actor => typeof actor === 'object' && actor.image) && (
                  <p className="text-green-400 text-sm mt-2">
                    ✅ Les images du casting seront conservées depuis TMDB
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white mb-2">URL de l'affiche *</label>
                  <input
                    type="url"
                    name="poster"
                    value={formData.poster}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white mb-2">URL de la bande-annonce</label>
                  <input
                    type="url"
                    name="trailer"
                    value={formData.trailer}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-white mb-2">Méthode de téléchargement vidéo</label>
                <div className="flex space-x-4 mb-4">
                  <button
                    type="button"
                    onClick={() => setUploadMethod('direct')}
                    className={`px-4 py-2 rounded-lg ${
                      uploadMethod === 'direct'
                        ? 'bg-gray-700 text-white'
                        : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    URL directe
                  </button>
                  <button
                    type="button"
                    onClick={() => setUploadMethod('voe')}
                    className={`px-4 py-2 rounded-lg ${
                      uploadMethod === 'voe'
                        ? 'bg-gray-700 text-white'
                        : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    Saisie manuelle
                  </button>
                </div>

                {uploadMethod === 'direct' ? (
                  <div className="space-y-2">
                    <input
                      type="url"
                      name="videoUrl"
                      value={formData.videoUrl}
                      onChange={handleInputChange}
                    placeholder="https://iframe.mediadelivery.net/play/580230/2449ac8c-9d76-42b7-b044-9057c839df5c (l'ID vidéo sera extrait automatiquement)"
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                  />
                  {formData.videoCode && (
                    <p className="text-green-400 text-sm">
                      ✅ ID vidéo extrait : {formData.videoCode}
                    </p>
                  )}
                  <p className="text-gray-400 text-xs">
                    💡 Collez l'URL Bunny Stream ou simplement l'ID vidéo (UUID)
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={voeUrl}
                    onChange={(e) => setVoeUrl(e.target.value)}
                    placeholder="Entrez l'ID vidéo Bunny Stream (UUID) ou l'URL complète"
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={handleVoeUpload}
                    disabled={uploading}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 disabled:opacity-50"
                  >
                    {uploading ? 'Enregistrement...' : 'Enregistrer l\'ID vidéo'}
                  </button>
                  {formData.videoCode && (
                    <p className="text-green-400">ID vidéo Bunny Stream : {formData.videoCode}</p>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-white mb-2">Durée (minutes) *</label>
                <input
                  type="number"
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-white mb-2">Note IMDb</label>
                <input
                  type="number"
                  name="rating.imdb"
                  value={formData.rating.imdb}
                  onChange={handleInputChange}
                  step="0.1"
                  min="0"
                  max="5"
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                />
              </div>
              <div>
                <label className="block text-white mb-2">Langue</label>
                <input
                  type="text"
                  name="language"
                  value="Français"
                  className="w-full px-4 py-2 bg-gray-600 text-gray-400 rounded-lg cursor-not-allowed"
                  disabled
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="isTrending"
                  id="isTrending"
                  checked={formData.isTrending}
                  onChange={(e) => setFormData(prev => ({ ...prev, isTrending: e.target.checked }))}
                  className="w-5 h-5 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="isTrending" className="text-white cursor-pointer">
                  Afficher sur la page d'accueil (Tendances)
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="isNewRelease"
                  id="isNewRelease"
                  checked={formData.isNewRelease}
                  onChange={(e) => setFormData(prev => ({ ...prev, isNewRelease: e.target.checked }))}
                  className="w-5 h-5 text-green-600 bg-gray-700 border-gray-600 rounded focus:ring-green-500"
                />
                <label htmlFor="isNewRelease" className="text-white cursor-pointer">
                  Afficher comme Nouveauté
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="isAnimation"
                  id="isAnimation"
                  checked={formData.isAnimation}
                  onChange={(e) => setFormData(prev => ({ ...prev, isAnimation: e.target.checked }))}
                  className="w-5 h-5 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500"
                />
                <label htmlFor="isAnimation" className="text-white cursor-pointer">
                  Animation (utilise la bibliothèque Animations Bunny)
                </label>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={uploading}
                className="px-6 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition font-semibold disabled:opacity-50"
              >
                {uploading ? 'Enregistrement...' : editingMovie ? 'Mettre à jour le Film' : 'Ajouter le Film'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingMovie(null);
                }}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition"
              >
                Annuler
              </button>
            </div>
            </form>
          </div>
        )}

        {/* Movies List */}
        <div className="bg-[#1A1A1A] rounded-lg overflow-hidden">
          {/* Search Bar */}
          <div className="px-6 py-4 border-b border-gray-700">
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Rechercher des films par titre, genre, réalisateur ou année..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div className="text-gray-400 text-sm">
                {totalItems} film{totalItems !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="px-6 py-4 text-left text-white">Titre</th>
                  <th className="px-6 py-4 text-left text-white">Année</th>
                  <th className="px-6 py-4 text-left text-white">Genre</th>
                  <th className="px-6 py-4 text-left text-white">Tendances</th>
                  <th className="px-6 py-4 text-left text-white">Nouveauté</th>
                  <th className="px-6 py-4 text-left text-white">Code Vidéo</th>
                  <th className="px-6 py-4 text-left text-white">Actions</th>
                </tr>
              </thead>
              <tbody>
                {movies.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-gray-400">
                      {searchTerm ? 'Aucun film trouvé correspondant à votre recherche.' : 'Aucun film disponible. Ajoutez votre premier film !'}
                    </td>
                  </tr>
                ) : (
                  movies.map((movie) => (
                    <tr key={movie._id} className="border-b border-gray-700">
                      <td className="px-6 py-4 text-white">{movie.title}</td>
                      <td className="px-6 py-4 text-gray-300">{movie.releaseYear}</td>
                      <td className="px-6 py-4 text-gray-300">{movie.genre?.join(', ')}</td>
                      <td className="px-6 py-4 text-gray-300">
                        {movie.isTrending ? (
                          <span className="px-2 py-1 bg-yellow-500 text-black rounded text-xs font-semibold">Tendances</span>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-gray-300">
                        {movie.isNewRelease ? (
                          <span className="px-2 py-1 bg-green-500 text-black rounded text-xs font-semibold">Nouveau</span>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-gray-300">{movie.videoCode || 'N/A'}</td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(movie)}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Modifier
                          </button>
                          <button
                            onClick={() => handleDelete(movie._id)}
                            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                          >
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between">
              <div className="text-gray-400 text-sm">
                Affichage de {(currentPage - 1) * moviesPerPage + 1} à {Math.min(currentPage * moviesPerPage, totalItems)} sur {totalItems} film{totalItems !== 1 ? 's' : ''}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Précédent
                </button>
                
                {/* Page Numbers */}
                <div className="flex space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => paginate(pageNum)}
                        className={`px-4 py-2 rounded-lg transition ${
                          currentPage === pageNum
                            ? 'bg-white text-gray-900 font-semibold'
                            : 'bg-gray-700 text-white hover:bg-gray-600'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Movies;
